import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  getCloudinaryUrl,
  Image,
  Modal,
  ModalDialogue,
  theme,
} from '@sg/garnish';
import {
  useCustomizationContext,
  type useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { INGREDIENT_IMAGE_WIDTH } from '../../../../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationOutOfStockModal = (props: Props) => {
  const { actor, isVisible, onRequestClose, setScrollTargetId } = props;

  const { formatMessage } = useIntl();

  const { outOfStockIngredients, product, restaurant } =
    useCustomizationContext({ actor });

  // ─────────────────────────────────────────────────────────────────────

  const handleCta = useCallback(() => {
    const firstIngredient = outOfStockIngredients[0];
    const groupId = firstIngredient.modificationGroupId;

    onRequestClose();
    setScrollTargetId?.(groupId);
  }, [outOfStockIngredients, onRequestClose, setScrollTargetId]);

  // ─────────────────────────────────────────────────────────────────────

  const isMissingMultipleIngredients = outOfStockIngredients.length > 1;

  const a11y = formatMessage(messages.outOfStockA11y);

  const body = formatMessage(
    isMissingMultipleIngredients
      ? messages.outOfStockBodyPlural
      : messages.outOfStockBodySingular,
    { product: product?.name, restaurant: restaurant?.name },
  );

  const cta = formatMessage(
    isMissingMultipleIngredients
      ? messages.outOfStockCtaPlural
      : messages.outOfStockCtaSingular,
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      testID="pdp-modals.out-of-stock"
      size="medium"
      visible={isVisible}
      accessibilityLabel={a11y}
    >
      <SafeAreaView edges={['bottom']}>
        <Modal.BodyText sizeMatch={['22']}>{body}</Modal.BodyText>

        <View style={styles.content}>
          {outOfStockIngredients.map((modification) => {
            const { ingredient } = modification;

            if (!ingredient) return null;

            const ingredientAssetUrl = ingredient.asset.url;
            const ingredientImage = getCloudinaryUrl(ingredientAssetUrl, {
              crop: 'scale',
              width: INGREDIENT_IMAGE_WIDTH,
            });

            return (
              <View key={ingredient.name} style={styles.ingredient}>
                <Image source={ingredientImage} style={styles.image} />
                <BodyText sizeMatch={['18']}>{ingredient.name}</BodyText>
              </View>
            );
          })}
        </View>

        <Modal.Footer style={styles.footer} withoutTopBorder>
          <Button
            style={styles.button}
            size="large-wide"
            palette="primaryOutline"
            onPress={handleCta}
          >
            {cta}
          </Button>
        </Modal.Footer>
      </SafeAreaView>
    </ModalDialogue>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: theme.spacing['4'],
    marginBottom: theme.spacing['6'],
    gap: theme.spacing['2'],
  },
  ingredient: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  footer: {
    alignItems: 'center',
  },
  button: {
    paddingHorizontal: theme.spacing['16'],
  },
  image: {
    width: 30,
    height: 30,
    borderRadius: 30,
    backgroundColor: theme.colors.LIGHT,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  outOfStockA11y: {
    defaultMessage: 'Out of Stock',
    description: 'Customization | Out of Stock | A11y',
  },
  outOfStockBodySingular: {
    defaultMessage:
      'This item is usually in the {product}, but {restaurant} is currently out of stock. Thanks for understanding!',
    description: 'Customization | Out of Stock | Body (Singular)',
  },
  outOfStockBodyPlural: {
    defaultMessage:
      'These items are usually in the {product}, but {restaurant} is currently out of stock. Thanks for understanding!',
    description: 'Customization | Out of Stock | Body (Plural)',
  },
  outOfStockCtaSingular: {
    defaultMessage: 'Choose a new ingredient',
    description: 'Customization | Out of Stock | CTA (Singular)',
  },
  outOfStockCtaPlural: {
    defaultMessage: 'Choose new ingredients',
    description: 'Customization | Out of Stock | CTA (Plural)',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  actor: ReturnType<typeof useInitCustomizationMachine>;
  onRequestClose: () => void;
  setScrollTargetId?: (id: string) => void;
};
